import { useContext, useEffect, useState } from 'react';
import { useQuery } from 'utils/hooks/useQuery';
import { UserContext } from 'context/User/UserContext';
import Base from '../Base/Base';
import axiosInstance from '../../services/axios/axios';
import ResultTable from './components/ResultTable';
import parse from 'autosuggest-highlight/parse';
import match from 'autosuggest-highlight/match';
import PersonSearch from '../../components/UI/Input/PersonSearch/PersonSearch';
import {
	Body1,
	Subtitle1,
} from '../../components/UI/MDC/Typography/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import classes from './UpnEsoLookup.module.scss';
import { fetchUserData } from './_api.js';
import { UPN_ESO_LOOKUP_MODES } from './_const';
import { compareFn } from 'utils/objects/ObjectUtil';

export default function UpnEsoLookup() {
	/**
	 * @type {import('context/User/UserContext').ContextUser}
	 */
	const contextUser = useContext(UserContext);
	const user =
		contextUser.userInfo.impersonationProfile ??
		contextUser.userInfo.userProfile;

	const mode = useQuery().get('mode') ?? 'self';
	const [sSelectedUser, setSelectedUser] = useState();
	const [staff, setStaff] = useState();
	const [sIsLoading, setIsLoading] = useState(true);
	const [sIsSearching, setIsSearching] = useState(false);
	const [errorMessage, setErrorMessage] = useState('');
	const [rows, setRows] = useState([]);
	const [isExpanded, setIsExpanded] = useState(true);

	useEffect(() => {
		const render = async user => {
			setIsLoading(true);
			setSelectedUser(null);
			switch (mode) {
				case UPN_ESO_LOOKUP_MODES.SELF_LOOKUP:
					setIsLoading(false);
					break;
				case UPN_ESO_LOOKUP_MODES.MANAGER_LOOKUP:
					const response = await axiosInstance.get(
						'api/Eso/DirectReports',
						{
							params: {
								esoID: user?.ESOID,
							},
						}
					);
					const staff = response?.data?.payload
						?.map(s => {
							return {
								label: s?.Cells['Name']?.Value,
								familiarEsoId:
									s?.Cells['UPMC_E-Sign-On_ID']?.Value,
							};
						})
						.sort((a, b) => compareFn(a, b, 'label'));
					setStaff(staff);
					setIsLoading(false);
					break;
				case UPN_ESO_LOOKUP_MODES.HELP_DESK_LOOKUP:
					setIsLoading(false);
					break;
				default:
					break;
			}
		};

		if (user?.ID) {
			render(user);
		}
	}, [user, mode]);

	const getView = () => {
		switch (mode) {
			case UPN_ESO_LOOKUP_MODES.SELF_LOOKUP:
				return <ResultTable user={user} />;
			case UPN_ESO_LOOKUP_MODES.MANAGER_LOOKUP:
				return staff?.length > 0 ? (
					<>
						<Autocomplete
							options={staff}
							disabled={!(staff && staff.length > 0)}
							disablePortal={true}
							filterOptions={(options, { inputValue }) => {
								if (!inputValue) {
									return options;
								}
								const newOptions = options
									.filter(opt =>
										opt.label
											.toLowerCase()
											.includes(inputValue.toLowerCase())
									)
									.sort();
								return newOptions;
							}}
							disableClearable
							renderInput={params => (
								<TextField {...params} label='Staff' />
							)}
							renderOption={(props, option, { inputValue }) => {
								const optionStr = option.label;
								const matches = match(optionStr, inputValue);
								const parts = parse(optionStr, matches);
								return (
									<li {...props}>
										<div>
											{parts.map((part, index) => (
												<span
													key={index}
													className={{
														fontWeight:
															part.highlight
																? 700
																: 400,
													}}
												>
													{part.text}
												</span>
											))}
										</div>
									</li>
								);
							}}
							onChange={(_, newValue) => {
								newValue &&
									fetchUserData(newValue).then(response =>
										setSelectedUser(response)
									);
							}}
							autoComplete
						/>
						{sSelectedUser && <ResultTable user={sSelectedUser} />}
					</>
				) : (
					<Subtitle1>You have no staff to display.</Subtitle1>
				);
			case UPN_ESO_LOOKUP_MODES.HELP_DESK_LOOKUP:
				return (
					<>
						<Accordion
							expanded={isExpanded}
							onChange={() => setIsExpanded(!isExpanded)}
						>
							<AccordionSummary expandIcon={<ExpandMoreIcon />}>
								<Body1>Search For User</Body1>
							</AccordionSummary>
							<AccordionDetails>
								<PersonSearch
									textAlign='center'
									className={classes['person-search']}
									options={{ noChips: true }}
									shouldAutoScroll
									dataTable={{
										headers: [],
										label: 'Search Results (Please select one)',
										onRowClickedHandler: packet => {
											setSelectedUser();
											fetchUserData({
												familiarEsoId:
													packet.payload.Cells[
														'FAMILIAR_ESO_ID'
													].Value,
											}).then(response =>
												setSelectedUser(response)
											).finally(() => {
												setIsExpanded(false);
											});
										},
										rows: rows,
									}}
									errorMessage={errorMessage}
									isLoading={sIsSearching}
									onButtonClickedHandler={packet => {
										setRows([]);
										setIsSearching(true);
										axiosInstance
											.post(
												'api/Components/PersonSearch',
												packet
											)
											.then(response => {
												switch (response.status) {
													case 200:
														switch (
															response.data
																.responseCode
														) {
															case 200:
																setRows(
																	response
																		.data
																		.payload
																);
																setErrorMessage(
																	''
																);
																break;
															default:
																setErrorMessage(
																	response
																		.data
																		.messages[0]
																		.Value
																);
														}
														break;
													default:
														break;
												}
											})
											.finally(() => {
												setIsSearching(false);
											});
									}}
									requestTarget='ESO_UPN_LOOKUP'
									withoutFormik={true}
								/>
							</AccordionDetails>
						</Accordion>
						{sSelectedUser && <ResultTable user={sSelectedUser} />}
					</>
				);
			default:
				break;
		}
	};

	return (
		<Base loading={sIsLoading} title='UPN and ESO Lookup'>
			{getView()}
		</Base>
	);
}
